import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ChartPage from "./pages/ChartPage";
import BrokersPage from "./pages/BrokersPage";
import NotificationsPage from "./pages/NotificationsPage";
import StrategiesPage from "./pages/StrategiesPage";
import DeploymentsPage from "./pages/DeploymentsPage";
import DeploymentCreatePage from "./pages/DeploymentCreatePage";
import DeploymentDetailPage from "./pages/DeploymentDetailPage";
import DataPage from "./pages/DataPage";
import BacktestsPage from "./pages/BacktestsPage";
import BacktestDetailPage from "./pages/BacktestDetailPage";
import ZerodhaCallbackPage from "./pages/ZerodhaCallbackPage";
import UpstoxCallbackPage from "./pages/UpstoxCallbackPage";
import AdminUsersPage from "./pages/admin/UsersPage";
import AdminUserDetailPage from "./pages/admin/UserDetailPage";
import AdminLiveNowPage from "./pages/admin/LiveNowPage";
import RequireAdmin from "./components/RequireAdmin";
import RequireFeature from "./components/RequireFeature";
import { useAuth } from "./lib/auth";
import { setUnauthorizedHandler } from "./lib/api";

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

export default function AppRouter() {
  const { clearSession } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearSession();
      navigate("/login");
    });
  }, [clearSession, navigate]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Zerodha redirects here after Kite login; runs without RequireAuth because
          the popup may briefly outlive any session state we have. The page only
          reads request_token from the URL and postMessages it to the opener. */}
      <Route path="/zerodha-callback" element={<ZerodhaCallbackPage />} />
      {/* Same contract as the Zerodha callback, for Upstox's OAuth ?code=. */}
      <Route path="/upstox-callback" element={<UpstoxCallbackPage />} />
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/chart" element={<ChartPage />} />
        <Route path="/deployments" element={<DeploymentsPage />} />
        <Route path="/deployments/new" element={<DeploymentCreatePage />} />
        <Route path="/deployments/:id/edit" element={<DeploymentCreatePage />} />
        <Route path="/deployments/:id" element={<DeploymentDetailPage />} />
        <Route path="/strategies" element={<StrategiesPage />} />
        <Route path="/brokers" element={<BrokersPage />} />
        <Route path="/settings/notifications" element={<NotificationsPage />} />
        <Route
          path="/data"
          element={
            <RequireFeature feature="backfill">
              <DataPage />
            </RequireFeature>
          }
        />
        <Route
          path="/backtests"
          element={
            <RequireFeature feature="backtest">
              <BacktestsPage />
            </RequireFeature>
          }
        />
        <Route
          path="/backtests/:id"
          element={
            <RequireFeature feature="backtest">
              <BacktestDetailPage />
            </RequireFeature>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequireAdmin>
              <AdminUsersPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/live"
          element={
            <RequireAdmin>
              <AdminLiveNowPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/users/:id"
          element={
            <RequireAdmin>
              <AdminUserDetailPage />
            </RequireAdmin>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
